const template = document.createElement("template");

template.innerHTML = `
  <style data-role="base">
    :host {
      --bsp-bg: #fbfcfe;
      --bsp-shell-start: #ffffff;
      --bsp-shell-end: #fbfcfe;
      --bsp-surface: #ffffff;
      --bsp-surface-raised: #f8fafc;
      --bsp-panel: #ffffff;
      --bsp-card: #ffffff;
      --bsp-text: #18223d;
      --bsp-title: #16213a;
      --bsp-muted: #72809a;
      --bsp-soft: #f1f4f9;
      --bsp-border: #e4e9f2;
      --bsp-border-strong: #dce8f7;
      --bsp-green: #20b347;
      --bsp-green-strong: #159437;
      --bsp-green-soft: #eaf9ee;
      --bsp-green-text: #159437;
      --bsp-green-border: #d9eee0;
      --bsp-danger: #df3d3b;
      --bsp-shadow: 0 16px 36px rgba(24, 34, 61, 0.08);
      --bsp-shadow-soft: 0 8px 20px rgba(24, 34, 61, 0.06);
      --bsp-focus: 0 0 0 3px rgba(32, 179, 71, 0.22);
      --bsp-radius: 12px;
      --bsp-font: "Assistant", "Heebo", sans-serif;
      display: block;
      color: var(--bsp-text);
      font-family: var(--bsp-font);
      direction: rtl;
      color-scheme: light;
    }

    :host([theme="dark"]) {
      --bsp-bg: #1c1f26;
      --bsp-shell-start: #151920;
      --bsp-shell-end: #1c1f26;
      --bsp-surface: #1c1f26;
      --bsp-surface-raised: #242936;
      --bsp-panel: #20242d;
      --bsp-card: #20242d;
      --bsp-text: #f4f7fb;
      --bsp-title: #eef3fb;
      --bsp-muted: #b4bdc9;
      --bsp-soft: #2a303a;
      --bsp-border: #2c313a;
      --bsp-border-strong: #343b47;
      --bsp-green: #20b347;
      --bsp-green-strong: #159437;
      --bsp-green-soft: rgba(32, 179, 71, 0.16);
      --bsp-green-text: #8ce09c;
      --bsp-green-border: rgba(32, 179, 71, 0.28);
      --bsp-shadow: none;
      --bsp-shadow-soft: none;
      --bsp-focus: 0 0 0 3px rgba(32, 179, 71, 0.28);
      color-scheme: dark;
    }

    @media (prefers-color-scheme: dark) {
      :host(:not([theme="light"])) {
        --bsp-bg: #1c1f26;
        --bsp-shell-start: #151920;
        --bsp-shell-end: #1c1f26;
        --bsp-surface: #1c1f26;
        --bsp-surface-raised: #242936;
        --bsp-panel: #20242d;
        --bsp-card: #20242d;
        --bsp-text: #f4f7fb;
        --bsp-title: #eef3fb;
        --bsp-muted: #b4bdc9;
        --bsp-soft: #2a303a;
        --bsp-border: #2c313a;
        --bsp-border-strong: #343b47;
        --bsp-green: #20b347;
        --bsp-green-strong: #159437;
        --bsp-green-soft: rgba(32, 179, 71, 0.16);
        --bsp-green-text: #8ce09c;
        --bsp-green-border: rgba(32, 179, 71, 0.28);
        --bsp-shadow: none;
        --bsp-shadow-soft: none;
        --bsp-focus: 0 0 0 3px rgba(32, 179, 71, 0.28);
        color-scheme: dark;
      }
    }

    * {
      box-sizing: border-box;
    }

    button,
    input {
      font: inherit;
    }

    button {
      cursor: pointer;
    }

    .shell {
      border-radius: 1rem;
      min-height: 100%;
      background:
        radial-gradient(circle at 12% 0%, rgba(32, 179, 71, 0.06), transparent 30%),
        linear-gradient(180deg, var(--bsp-shell-start) 0%, var(--bsp-shell-end) 100%);
      padding: 0 0 28px;
    }

    :host([theme="dark"]) .shell {
      background:
        radial-gradient(circle at 12% 0%, rgba(32, 179, 71, 0.11), transparent 30%),
        linear-gradient(180deg, var(--bsp-shell-start) 0%, var(--bsp-shell-end) 100%);
    }

    @media (prefers-color-scheme: dark) {
      :host(:not([theme="light"])) .shell {
        background:
          radial-gradient(circle at 12% 0%, rgba(32, 179, 71, 0.11), transparent 30%),
          linear-gradient(180deg, var(--bsp-shell-start) 0%, var(--bsp-shell-end) 100%);
      }
    }

    .content {
      width: min(100%, 1120px);
      margin: 0 auto;
      padding: 38px clamp(16px, 3vw, 28px) 0;
    }

    .intro {
      text-align: right;
      margin: 0 0 28px;
    }

    .intro h2 {
      margin: 0;
      color: var(--bsp-title);
      font-size: clamp(26px, 3vw, 34px);
      line-height: 1.18;
      font-weight: 800;
      letter-spacing: 0;
    }

    .intro p {
      margin: 10px 0 0;
      color: var(--bsp-muted);
      font-size: 15px;
      line-height: 1.6;
    }

    .plans-grid {
      direction: ltr;
      display: grid;
      grid-template-columns: minmax(260px, 294px) minmax(0, 1fr);
      grid-template-areas: "summary models";
      gap: 28px;
      align-items: stretch;
    }

    .card,
    .panel {
      border: 1px solid var(--bsp-border);
      border-radius: var(--bsp-radius);
      background: var(--bsp-panel);
      box-shadow: var(--bsp-shadow);
    }

    .panel {
      padding: 20px;
    }

    .summary {
      grid-area: summary;
      padding: 22px 20px 18px;
      display: flex;
      flex-direction: column;
      min-height: 292px;
      text-align: center;
      direction: rtl;
      border-color: var(--bsp-green-border);
      background: linear-gradient(135deg, var(--bsp-panel), var(--bsp-green-soft));
      box-shadow: var(--bsp-shadow-soft);
    }

    .summary h3,
    .section-title,
    .status-title {
      margin: 0;
      font-size: 20px;
      font-weight: 800;
      letter-spacing: 0;
    }

    .summary-subtitle {
      margin: 10px 0 0;
      color: var(--bsp-muted);
      font-size: 14px;
    }

    .summary-price {
      margin: auto 0 18px;
      color: var(--bsp-green);
      line-height: 1.12;
      font-weight: 800;
    }

    .summary-price strong {
      font-size: clamp(32px, 4vw, 42px);
    }

    .summary-price span {
      display: block;
      margin-top: 4px;
      font-size: 16px;
      color: var(--bsp-text);
      font-weight: 500;
    }

    .summary-divider {
      height: 1px;
      background: var(--bsp-border);
      margin: 0 0 16px;
    }

    .summary-date {
      direction: ltr;
      display: grid;
      grid-template-columns: 32px 1fr;
      align-items: center;
      gap: 10px;
      text-align: right;
      margin-bottom: 14px;
      color: var(--bsp-muted);
      font-size: 13px;
    }

    .summary-date > span:last-child {
      direction: rtl;
    }

    .summary-date strong {
      display: block;
      color: var(--bsp-text);
      margin-top: 2px;
      font-size: 14px;
    }

    .models {
      grid-area: models;
      direction: rtl;
    }

    .section-head {
      text-align: right;
      margin-bottom: 18px;
    }

    .section-head p {
      margin: 10px 0 0;
      color: var(--bsp-muted);
      font-size: 14px;
      line-height: 1.6;
    }

    .model-list {
      display: grid;
      gap: 16px;
    }

    .model-card {
      direction: ltr;
      min-height: 112px;
      border: 1px solid var(--bsp-border);
      border-radius: var(--bsp-radius);
      background: var(--bsp-card);
      display: grid;
      grid-template-columns: 86px 110px minmax(0, 1fr) 82px;
      grid-template-areas: "toggle price copy avatar";
      align-items: center;
      gap: 16px;
      padding: 14px 18px;
      transition: border-color 160ms ease, background 160ms ease;
    }

    .model-card:has(input:focus-visible) {
      box-shadow: var(--bsp-focus);
    }

    .model-card:hover {
      border-color: var(--bsp-border-strong);
    }

    .avatar {
      grid-area: avatar;
      inline-size: 68px;
      block-size: 72px;
      color: var(--bsp-text);
    }

    .model-copy {
      grid-area: copy;
      min-width: 0;
      text-align: right;
      direction: rtl;
    }

    .model-copy h4 {
      margin: 0 0 7px;
      font-size: 17px;
      line-height: 1.25;
      font-weight: 800;
      direction: ltr;
      text-align: right;
    }

    .model-copy p {
      margin: 0;
      color: var(--bsp-muted);
      font-size: 14px;
      line-height: 1.55;
    }

    .price {
      grid-area: price;
      text-align: right;
      line-height: 1.25;
      direction: rtl;
    }

    .price strong {
      display: block;
      font-size: 18px;
      color: var(--bsp-text);
      font-weight: 800;
    }

    .price span {
      color: var(--bsp-muted);
      font-size: 13px;
    }

    .switch {
      grid-area: toggle;
      width: 58px;
      height: 34px;
      position: relative;
      justify-self: center;
    }

    .switch input {
      position: absolute;
      inset: 0;
      opacity: 0;
      margin: 0;
      cursor: pointer;
    }

    .switch span {
      position: absolute;
      inset: 0;
      border-radius: 999px;
      background: #cfd6df;
      box-shadow: inset 0 1px 2px rgba(17, 24, 39, 0.18);
      transition: background 180ms ease;
    }

    .switch span::after {
      content: "";
      position: absolute;
      top: 3px;
      left: 3px;
      width: 28px;
      height: 28px;
      border-radius: 999px;
      background: #fff;
      box-shadow: 0 2px 6px rgba(17, 24, 39, 0.24);
      transition: transform 180ms ease;
    }

    .switch input:checked + span {
      background: linear-gradient(180deg, #35b64b, #27993e);
    }

    .switch input:checked + span::after {
      transform: translateX(24px);
    }

    .payment {
      margin-top: 16px;
      padding: 20px 22px 16px;
    }

    .payment-card {
      direction: ltr;
      margin-top: 16px;
      min-height: 98px;
      border: 1px solid var(--bsp-border);
      border-radius: var(--bsp-radius);
      display: grid;
      grid-template-columns: 190px minmax(0, 1fr) 160px;
      grid-template-areas: "button assurance provider";
      align-items: center;
      gap: 20px;
      padding: 16px 18px;
      background: var(--bsp-card);
    }

    .provider {
      grid-area: provider;
      text-align: center;
      direction: ltr;
    }

    .grow-logo {
      font-size: 28px;
      font-weight: 900;
      line-height: 1;
      color: #4021a7;
      letter-spacing: 0;
      direction: ltr;
      font-family: Arial, sans-serif;
    }

    :host([theme="dark"]) .grow-logo {
      color: #9b87ff;
    }

    @media (prefers-color-scheme: dark) {
      :host(:not([theme="light"])) .grow-logo {
        color: #9b87ff;
      }
    }

    .pci {
      margin-top: 8px;
      color: var(--bsp-muted);
      font-size: 12px;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      direction: ltr;
    }

    .assurance {
      grid-area: assurance;
      display: grid;
      justify-items: start;
      gap: 9px;
      color: var(--bsp-muted);
      font-size: 14px;
      line-height: 1.5;
      text-align: right;
      direction: rtl;
    }

    .secure-line {
      color: var(--bsp-muted);
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .secure-line svg {
      color: var(--bsp-green);
    }

    .payment-button {
      grid-area: button;
      justify-self: start;
      direction: rtl;
      min-width: 154px;
      min-height: 36px;
      border: 0;
      border-radius: 4px;
      color: #fff;
      font-weight: 700;
      background: linear-gradient(180deg, #202020, #070707);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
    }

    .payment-button:focus-visible {
      outline: none;
      box-shadow: var(--bsp-focus);
    }

    .billing-status {
      margin-top: 14px;
      padding: 22px 20px 28px;
      box-shadow: var(--bsp-shadow-soft);
    }

    .status-title {
      text-align: right;
      margin-bottom: 18px;
    }

    .status-grid {
      direction: ltr;
      min-height: 80px;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-template-areas: "status amount date";
      align-items: center;
      text-align: center;
      border-radius: var(--bsp-radius);
    }

    .status-item {
      min-width: 0;
      padding: 0 22px;
      color: var(--bsp-muted);
      font-size: 13px;
      direction: rtl;
    }

    .status-item strong {
      display: block;
      margin-top: 8px;
      color: var(--bsp-text);
      font-size: 15px;
      font-weight: 800;
    }

    .status-date {
      grid-area: date;
    }

    .status-amount {
      grid-area: amount;
    }

    .status-state {
      grid-area: status;
    }

    .status-state,
    .status-amount {
      border-right: 1px solid var(--bsp-border);
    }

    .pill {
      border: 0;
      border-radius: 999px;
      background: var(--bsp-green-soft);
      color: var(--bsp-green-text);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 28px;
      min-width: 76px;
      padding: 0 12px;
      font-size: 13px;
      font-weight: 800;
    }

    .icon {
      display: inline-flex;
      inline-size: 18px;
      block-size: 18px;
      flex: 0 0 auto;
    }

    .icon svg,
    .avatar svg {
      inline-size: 100%;
      block-size: 100%;
      display: block;
    }

    @media (max-width: 850px) {
      .content {
        padding-top: 28px;
      }

      .plans-grid {
        grid-template-columns: 1fr;
        grid-template-areas:
          "models"
          "summary";
        gap: 16px;
      }

      .summary {
        min-height: auto;
      }

      .summary-price {
        margin: 24px 0 18px;
      }

      .payment-card {
        grid-template-columns: 1fr;
        grid-template-areas:
          "provider"
          "assurance"
          "button";
        justify-items: stretch;
        text-align: center;
      }

      .assurance {
        justify-items: center;
        text-align: center;
      }

      .payment-button {
        justify-self: stretch;
      }
    }

    @media (max-width: 600px) {
      .intro {
        margin-bottom: 18px;
      }

      .panel,
      .payment,
      .billing-status {
        padding: 16px;
      }

      .model-card {
        grid-template-columns: 58px minmax(0, 1fr) 62px;
        grid-template-areas:
          "avatar copy toggle"
          "avatar price toggle";
        gap: 8px 12px;
        padding: 14px;
      }

      .avatar {
        inline-size: 54px;
        block-size: 58px;
      }

      .switch {
        width: 50px;
        height: 30px;
      }

      .switch span::after {
        width: 24px;
        height: 24px;
      }

      .switch input:checked + span::after {
        transform: translateX(20px);
      }

      .status-grid {
        grid-template-columns: 1fr;
        grid-template-areas:
          "date"
          "amount"
          "status";
        gap: 18px;
      }

      .status-item {
        padding: 0;
      }

      .status-state,
      .status-amount {
        border-right: 0;
        border-top: 1px solid var(--bsp-border);
        padding-top: 18px;
      }
    }
  </style>
  <section class="shell" data-testid="billing-settings-page">
    <main class="content" part="content">
      <header class="intro">
        <h2>הגדרות ותשלומים</h2>
        <p>נהל את המנויים, אמצעי התשלום והחיובים שלך.</p>
      </header>

      <div class="plans-grid">
        <aside class="card summary" part="summary-card">
          <h3>סיכום חודשי נוכחי</h3>
          <p class="summary-subtitle" data-role="active-count-label">2 מודולים פעילים</p>
          <div class="summary-price">
            <strong data-role="summary-price">₪138</strong>
            <span>לחודש לא כולל מעמ</span>
          </div>
          <div class="summary-divider" aria-hidden="true"></div>
          <div class="summary-date">
            <span class="icon" aria-hidden="true">${calendarIcon()}</span>
            <span>תאריך החיוב הבא<strong>${getLastDayOfCurrentMonthFormatted()}</strong></span>
          </div>
          <span class="pill" data-role="summary-status">
            <span aria-hidden="true">${checkIcon()}</span>
            סטטוס: פעיל
          </span>
        </aside>

        <section class="panel models" part="models-panel">
          <div class="section-head">
            <h3 class="section-title">מודולים פעילים</h3>
            <p>בחר את המודולים שברצונך להשתמש בהם.</p>
          </div>
          <div class="model-list" role="list">
            ${modelCard({
  id: "lite_schedule",
  title: "Shift Manager",
  description: "ניהול משמרות, מעקב שעות עבודה ותכנון צוות.",
  avatar: "clipboard",
})}
            ${modelCard({
  id: "tasks",
  title: "Task Manager",
  description: "ניהול משימות, מעקב ביצועים ובקרה תפעולית.",
  avatar: "megaphone",
})}
          </div>
        </section>
      </div>

      <section class="panel payment" part="payment-panel">
        <div class="section-head">
          <h3 class="section-title">אמצעי תשלום</h3>
          <p>נהל את אמצעי התשלום שלך בצורה מאובטחת.</p>
        </div>
        <div class="payment-card">
          <div class="provider">
            <div class="grow-logo" aria-label="Grow">Grow</div>
            <span class="pci">PCI DSS מאושר ${lockIcon()}</span>
          </div>
          <div class="assurance">
            <span class="secure-line">${shieldIcon()} תשלום מאובטח באמצעות Grow</span>
            <span>הנתונים שלך מוגנים ומאובטחים.</span>
          </div>
          <button class="payment-button" type="button" data-action="payment">עדכן אמצעי תשלום</button>
        </div>
      </section>

      <section class="panel billing-status" part="status-panel">
        <h3 class="status-title">סטטוס חיוב</h3>
        <div class="status-grid">
          <div class="status-item status-state">
            סטטוס
            <strong><span class="pill" data-role="billing-status">${checkIcon()} פעיל</span></strong>
          </div>
          <div class="status-item status-amount">
            סכום לחיוב הבא
            <strong data-role="status-price">₪138</strong>
          </div>
          <div class="status-item status-date">
            תאריך החיוב הבא
            <strong><span class="icon" aria-hidden="true">${calendarIcon()}</span> ${getLastDayOfCurrentMonthFormatted()}</strong>
          </div>
        </div>
      </section>

    </main>
  </section>
`;

function modelCard({ id, title, description, avatar }) {
  const label = title.replace(" Manager", "");
  return `
    <article class="model-card" role="listitem" part="model-card" data-plan="${id}">
      <label class="switch" aria-label="הפעלת ${label}">
        <input type="checkbox" value="${id}" checked data-plan-toggle>
        <span aria-hidden="true"></span>
      </label>
      <div class="price">
        <strong>₪69</strong>
        <span>לחודש</span>
      </div>
      <div class="model-copy">
        <h4>${title}</h4>
        <p>${description}</p>
      </div>
      <span class="avatar" aria-hidden="true">${avatar === "clipboard" ? clipboardPerson() : megaphonePerson()}</span>
    </article>
  `;
}

function getLastDayOfCurrentMonthFormatted() {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const dd = String(lastDay.getDate()).padStart(2, "0");
  const mm = String(lastDay.getMonth() + 1).padStart(2, "0");
  const yyyy = lastDay.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function getLastDayOfCurrentMonthISO() {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const dd = String(lastDay.getDate()).padStart(2, "0");
  const mm = String(lastDay.getMonth() + 1).padStart(2, "0");
  const yyyy = lastDay.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

function calendarIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4M16 2v4M3.5 9h17M5 5h14a1.5 1.5 0 0 1 1.5 1.5v14A1.5 1.5 0 0 1 19 22H5a1.5 1.5 0 0 1-1.5-1.5v-14A1.5 1.5 0 0 1 5 5Z"/></svg>`;
}

function checkIcon() {
  return `<svg viewBox="0 0 18 18" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="9" r="7"/><path d="m5.8 9 2 2 4.2-4.3"/></svg>`;
}

function shieldIcon() {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m8.8 12 2.1 2.1 4.4-4.5"/></svg>`;
}

function lockIcon() {
  return `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>`;
}

function clipboardPerson() {
  return `
    <svg viewBox="0 0 86 92" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="42" cy="27" r="15" fill="var(--bsp-surface)"/>
      <path d="M23 74c2-18 11-28 26-28 12 0 20 8 23 24"/>
      <path d="M33 23h12M54 23h9M31 29c2 3 8 3 10 0M54 29c2 3 8 3 10 0"/>
      <path d="M45 36c4 2 8 2 12 0"/>
      <path d="M24 48c-5-5-8-11-7-18M21 24c-3-3-4-7-3-12"/>
      <path d="M51 54v28M35 52l-5 27M64 59l9 18"/>
      <rect x="56" y="51" width="24" height="32" rx="3" fill="var(--bsp-surface)"/>
      <path d="M63 59h10M63 66h12M63 73h8"/>
      <path d="M36 10c3-6 17-7 23 0M23 16c5-7 20-10 34-7"/>
    </svg>
  `;
}

function megaphonePerson() {
  return `
    <svg viewBox="0 0 86 92" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M41 45c13 0 23 11 24 29M19 75c1-20 10-30 26-30"/>
      <circle cx="45" cy="27" r="15" fill="var(--bsp-surface)"/>
      <path d="M34 29c2 3 7 3 9 0M52 29c2 3 7 3 9 0M42 37c4 2 8 2 12-1"/>
      <path d="M31 16c5-10 24-12 31 1M60 15c7 2 10 9 8 18"/>
      <path d="M18 48h12l14-10v31L30 59H18Z" fill="var(--bsp-surface)"/>
      <path d="M45 46c5 2 7 7 7 12M55 42c7 5 10 12 9 20"/>
      <path d="M30 59l5 19M46 62l7 17M22 59l-4 14"/>
    </svg>
  `;
}

class BillingSettingsPage extends HTMLElement {
  static get observedAttributes() {
    return ["theme"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.append(template.content.cloneNode(true));
    this._activePlans = new Set(["lite_schedule", "tasks"]);
    this._pricePerPlan = 69;
    this._resolvedTheme = "light";
    this._themeObserver = null;
    this._inheritsTheme = false;
    this._applyingDetectedTheme = false;
    this.onThemeChange = null;
    this.onPlanChange = null;
  }

  get activePlans() {
    return [...this._activePlans];
  }

  set activePlans(value) {
    this._activePlans = new Set(Array.isArray(value) ? value : []);
    this._updateCheckboxes();
    this._updateTotals();
  }

  _updateCheckboxes() {
    const checkboxes = this.shadowRoot.querySelectorAll("[data-plan-toggle]");
    checkboxes.forEach((input) => {
      input.checked = this._activePlans.has(input.value);
    });
  }

  connectedCallback() {
    this._bindEvents();
    this.applyDetectedTheme();
    this._startThemeObserver();
    this._resolveTheme("connect");
    this._updateCheckboxes();
    this._updateTotals();
  }

  disconnectedCallback() {
    this._stopThemeObserver();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === "theme") {
      if (!this._applyingDetectedTheme) {
        this._inheritsTheme = false;
      }
      this._resolveTheme("attribute");
    }
  }

  get theme() {
    return this.normalizeThemeValue(this.getAttribute("theme")) || this.detectTheme();
  }

  set theme(value) {
    this.setTheme(value);
  }

  get resolvedTheme() {
    return this._resolvedTheme;
  }

  setTheme(value) {
    const next = this.normalizeThemeValue(value);
    if (next) {
      this.setAttribute("theme", next);
      return;
    }
    this.removeAttribute("theme");
  }

  applyDetectedTheme() {
    if (this.hasAttribute("theme") && !this._inheritsTheme) return;
    const theme = this.detectTheme();
    if (!theme) return;
    this._inheritsTheme = true;
    this._applyingDetectedTheme = true;
    this.setAttribute("theme", theme);
    this._applyingDetectedTheme = false;
  }

  detectTheme() {
    return this.findInheritedTheme();
  }

  findInheritedTheme() {
    const candidates = [];
    for (let node = this.parentElement; node; node = node.parentElement) {
      candidates.push(node);
    }
    if (globalThis.document?.documentElement) candidates.push(globalThis.document.documentElement);
    if (globalThis.document?.body) candidates.push(globalThis.document.body);

    for (const node of candidates) {
      const theme = this.normalizeThemeValue(node.getAttribute?.("theme") || node.getAttribute?.("data-theme") || "");
      if (theme) return theme;
      if (node.classList?.contains("dark")) return "dark";
      if (node.classList?.contains("light")) return "light";
    }

    return "";
  }

  normalizeThemeValue(value) {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "dark") return "dark";
    if (normalized === "light") return "light";
    return "";
  }

  _startThemeObserver() {
    this._stopThemeObserver();
    this._themeObserver = new MutationObserver(() => {
      this.applyDetectedTheme();
      this._resolveTheme("context");
    });

    const candidates = [];
    for (let node = this.parentElement; node; node = node.parentElement) {
      candidates.push(node);
    }
    if (globalThis.document?.documentElement) candidates.push(globalThis.document.documentElement);
    if (globalThis.document?.body) candidates.push(globalThis.document.body);

    for (const element of new Set(candidates.filter(Boolean))) {
      this._themeObserver.observe(element, {
        attributes: true,
        attributeFilter: ["theme", "data-theme", "class"],
      });
    }
  }

  _stopThemeObserver() {
    if (this._themeObserver) {
      this._themeObserver.disconnect();
      this._themeObserver = null;
    }
  }

  _bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;
    this.shadowRoot.addEventListener("change", (event) => {
      const input = event.target.closest("[data-plan-toggle]");
      if (!input) return;
      if (input.checked) {
        this._activePlans.add(input.value);
      } else {
        this._activePlans.delete(input.value);
      }
      this._updateTotals();
      this._emitPlanChange(input.value);
    });

    this.shadowRoot.addEventListener("click", (event) => {
      const paymentButton = event.target.closest('[data-action="payment"]');
      if (paymentButton) {
        this.dispatchEvent(new CustomEvent("paymentmethodrequest", {
          bubbles: true,
          composed: true,
          detail: this._currentBillingState(),
        }));
      }
    });
  }

  _resolveTheme(source) {
    const requested = this.theme;
    const next = requested === "dark" ? "dark" : "light";
    const previous = this._resolvedTheme;
    this._resolvedTheme = next;
    this.setAttribute("data-resolved-theme", next);

    if (previous !== next || source === "connect" || source === "attribute") {
      const detail = { theme: requested, resolvedTheme: next, source };
      this.dispatchEvent(new CustomEvent("themechange", {
        bubbles: true,
        composed: true,
        detail,
      }));
      if (typeof this.onThemeChange === "function") {
        this.onThemeChange(detail, this);
      }
    }
  }

  _updateTotals() {
    const activeCount = this._activePlans.size;
    const total = activeCount * this._pricePerPlan;
    const totalText = `₪${total}`;
    const statusLabel = activeCount > 0 ? "פעיל" : "לא פעיל";
    const countLabel = activeCount === 1 ? "מודול פעיל" : "מודולים פעילים";

    this.shadowRoot.querySelector('[data-role="active-count-label"]').textContent = `${activeCount} ${countLabel}`;
    this.shadowRoot.querySelector('[data-role="summary-price"]').textContent = totalText;
    this.shadowRoot.querySelector('[data-role="status-price"]').textContent = totalText;
    this.shadowRoot.querySelector('[data-role="summary-status"]').innerHTML = `${checkIcon()} סטטוס: ${statusLabel}`;
    this.shadowRoot.querySelector('[data-role="billing-status"]').innerHTML = `${checkIcon()} ${statusLabel}`;
  }

  _emitPlanChange(planId) {
    const detail = { ...this._currentBillingState(), changedPlan: planId };
    this.dispatchEvent(new CustomEvent("planchange", {
      bubbles: true,
      composed: true,
      detail,
    }));
    if (typeof this.onPlanChange === "function") {
      this.onPlanChange(detail, this);
    }
  }

  _emitSave(button) {
    const detail = this._currentBillingState();
    this.dispatchEvent(new CustomEvent("save", {
      bubbles: true,
      composed: true,
      detail,
    }));
    if (typeof this.onSave === "function") {
      this.onSave(detail, this);
    }

    const original = button.textContent;
    button.textContent = "נשמר";
    window.setTimeout(() => {
      button.textContent = original;
    }, 1200);
  }

  _currentBillingState() {
    return {
      activePlans: [...this._activePlans],
      activeCount: this._activePlans.size,
      pricePerPlan: this._pricePerPlan,
      total: this._activePlans.size * this._pricePerPlan,
      currency: "ILS",
      nextBillingDate: getLastDayOfCurrentMonthISO(),
      status: this._activePlans.size > 0 ? "active" : "inactive",
      theme: this.theme,
      resolvedTheme: this.resolvedTheme,
    };
  }
}

if (!customElements.get("billing-settings-page")) {
  customElements.define("billing-settings-page", BillingSettingsPage);
}

export { BillingSettingsPage };
